import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  SafeAreaView,
  StatusBar,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  FlatList,
  Dimensions,
  Alert,
  Modal,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  FontAwesome,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

import { Video } from 'expo-video';
import { Audio } from 'expo-audio'; // if you need audio
import * as Calendar from 'expo-calendar';
import * as Notifications from 'expo-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

// ─── Connection ──────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const jvStr = (v) => String(v ?? '').trim();
const jvArr = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {}
    return v.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};
const jvDate = (v) => {
  if (v instanceof Date) return v;
  if (!v) return new Date();
  const d = new Date(v);
  return isNaN(d.getTime()) ? new Date() : d;
};

// Map a DB row from /api/data/live_courses to the shape this screen expects.
const jvMapCourse = (row, idx) => {
  const price = Number(row.price ?? 0) || 0;
  return {
    id: jvStr(row.id ?? row.course_id) || `LVC${idx + 1}`,
    title: jvStr(row.title) || 'Live Session',
    subtitle: jvStr(row.subtitle) || jvStr(row.tagline) || '',
    instructor: jvStr(row.instructor) || jvStr(row.instructor_name) || 'JobEng Instructor',
    instructorTitle: jvStr(row.instructor_title) || '',
    date: jvDate(row.date || row.session_date || row.start_at),
    time: jvStr(row.time) || jvStr(row.session_time) || '',
    duration: jvStr(row.duration) || '',
    category: jvStr(row.category) || 'General',
    level: jvStr(row.level) || 'All Levels',
    price,
    isFree: row.is_free === true || row.isFree === true || price <= 0,
    enrolled: Number(row.enrolled ?? row.enrolled_count ?? 0) || 0,
    maxParticipants: Number(row.max_participants ?? 0) || 0,
    status: jvStr(row.status) || 'upcoming',
    color: jvStr(row.color) || '#0e4e96',
    icon: jvStr(row.icon) || 'video',
    description: jvStr(row.description) || '',
    topics: jvArr(row.topics),
    requirements: jvArr(row.requirements),
    features: jvArr(row.features),
    videoUrl: jvStr(row.video_url) || jvStr(row.videoUrl) || jvStr(row.live_url) || '',
    recordingUrl: jvStr(row.recording_url) || null,
    roomPassword: jvStr(row.room_password) || '',
    scheduleLink: jvStr(row.schedule_link) || '',
    materials: jvArr(row.materials),
    instructorImage: row.instructor_image || null,
    rating: Number(row.rating ?? 0) || 0,
    totalRatings: Number(row.total_ratings ?? 0) || 0,
    tags: jvArr(row.tags),
    isEnrolled: false,
    progress: Number(row.progress ?? 0) || 0,
  };
};

// Configure notifications
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

const LiveVideoCoursesScreen = ({ navigation, route }) => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [userEnrolled, setUserEnrolled] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Live Courses Data — curated seed used as fallback when the
  // live_courses table is empty or unavailable.
  const SEED_LIVE_COURSES = [
    {
      id: 'LVC1',
      title: 'Structural Analysis Live Workshop',
      subtitle: 'Real-time structural calculations with expert guidance',
      instructor: 'Dr. Ahmed Hassan, PhD',
      instructorTitle: 'Senior Structural Engineer',
      date: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours from now
      time: '7:00 PM - 9:00 PM',
      duration: '2 hours',
      category: 'Civil Engineering',
      level: 'Intermediate',
      price: 49,
      isFree: false,
      enrolled: 156,
      maxParticipants: 200,
      status: 'upcoming',
      color: '#0e4e96',
      icon: 'bridge',
      description: 'Join this live interactive workshop where we will solve real structural analysis problems together. Includes live Q&A session with industry expert.',
      topics: [
        'Finite Element Method Applications',
        'Dynamic Load Analysis',
        'Seismic Design Principles',
        'Real-time Problem Solving',
        'Interactive Q&A Session'
      ],
      requirements: [
        'Basic knowledge of structural analysis',
        'Calculator or laptop',
        'Stable internet connection'
      ],
      features: [
        'Live video streaming',
        'Interactive whiteboard',
        'Breakout rooms',
        'Screen sharing',
        'Recording available'
      ],
      videoUrl: 'https://meet.google.com/xyz-live-structural',
      recordingUrl: null,
      roomPassword: 'STRUCT2024',
      scheduleLink: 'https://calendar.google.com/event?action=TEMPLATE&tmeid=xyz',
      materials: [
        'Workshop Slides.pdf',
        'Sample Problems.xlsx',
        'Reference Materials.zip'
      ],
      instructorImage: null,
      rating: 4.9,
      totalRatings: 128,
      tags: ['Live', 'Interactive', 'Workshop', 'Q&A'],
      isEnrolled: false,
      progress: 0,
    },
    {
      id: 'LVC2',
      title: 'Python for Engineers - Live Coding Marathon',
      subtitle: '6-hour coding marathon with live problem solving',
      instructor: 'Prof. Sarah Johnson',
      instructorTitle: 'AI & Machine Learning Expert',
      date: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
      time: '6:00 PM - 12:00 AM',
      duration: '6 hours',
      category: 'Computer Engineering',
      level: 'Beginner to Intermediate',
      price: 0,
      isFree: true,
      enrolled: 289,
      maxParticipants: 500,
      status: 'upcoming',
      color: '#3776AB',
      icon: 'language-python',
      description: 'Participate in our live coding marathon! Work on real engineering problems using Python. Get instant feedback from instructors.',
      topics: [
        'NumPy for Engineering Calculations',
        'Data Visualization with Matplotlib',
        'Automating Engineering Tasks',
        'Machine Learning Basics',
        'Live Coding Challenges'
      ],
      requirements: [
        'Python installed',
        'Basic programming knowledge',
        'Jupyter Notebook or VS Code'
      ],
      features: [
        'Live coding session',
        'Real-time debugging',
        'Code sharing',
        'Instant feedback',
        'Certificate of participation'
      ],
      videoUrl: 'https://zoom.us/j/123456789',
      recordingUrl: null,
      roomPassword: 'PYTHON2024',
      scheduleLink: 'https://calendar.google.com/event?action=TEMPLATE&tmeid=abc',
      materials: [
        'Python Workshop Notebook.ipynb',
        'Engineering Problems Dataset.csv',
        'Useful Libraries Guide.pdf'
      ],
      instructorImage: null,
      rating: 4.8,
      totalRatings: 215,
      tags: ['Free', 'Coding', 'Marathon', 'Hands-on'],
      isEnrolled: true,
      progress: 0,
    },
    {
      id: 'LVC3',
      title: '🔴 LIVE NOW: CAD Design Challenge',
      subtitle: 'Real-time 3D modeling competition',
      instructor: 'Eng. Mohammed Ali',
      instructorTitle: 'CAD & 3D Design Specialist',
      date: new Date(), // Now
      time: '4:00 PM - 6:00 PM',
      duration: '2 hours',
      category: 'Mechanical Engineering',
      level: 'Intermediate',
      price: 29,
      isFree: false,
      enrolled: 94,
      maxParticipants: 150,
      status: 'live',
      color: '#FF6B35',
      icon: 'cube',
      description: 'Join our live CAD design challenge! Compete with other engineers in real-time 3D modeling. Get instant feedback from judges.',
      topics: [
        'Advanced 3D Modeling Techniques',
        'Assembly Design Best Practices',
        'Real-time Design Feedback',
        'Competition Rules Review',
        'Prize Announcement'
      ],
      requirements: [
        'Fusion 360 or SolidWorks',
        'Basic CAD knowledge',
        'Good internet connection'
      ],
      features: [
        'Live competition',
        'Real-time judging',
        'Screen sharing',
        'Instant feedback',
        'Prizes for winners'
      ],
      videoUrl: 'https://teams.microsoft.com/l/meetup-join/19:meeting_xyz',
      recordingUrl: null,
      roomPassword: 'CAD2024',
      scheduleLink: null,
      materials: [
        'Competition Brief.pdf',
        'Reference Models.zip',
        'Judging Criteria.pdf'
      ],
      instructorImage: null,
      rating: 4.7,
      totalRatings: 89,
      tags: ['Live Now', 'Competition', 'Real-time', 'Challenge'],
      isEnrolled: true,
      progress: 45,
    },
    {
      id: 'LVC4',
      title: 'Electrical Circuit Design Masterclass',
      subtitle: 'Advanced circuit simulation live session',
      instructor: 'Dr. Lisa Wang',
      instructorTitle: 'Electrical Engineering Professor',
      date: new Date(Date.now() + 48 * 60 * 60 * 1000), // 2 days from now
      time: '3:00 PM - 5:00 PM',
      duration: '2 hours',
      category: 'Electrical Engineering',
      level: 'Advanced',
      price: 79,
      isFree: false,
      enrolled: 67,
      maxParticipants: 100,
      status: 'upcoming',
      color: '#FFD700',
      icon: 'flash',
      description: 'Advanced electrical circuit design with live simulations using SPICE software. Troubleshoot circuits in real-time with expert guidance.',
      topics: [
        'Advanced Circuit Analysis',
        'SPICE Simulation Techniques',
        'Troubleshooting Methods',
        'Design Optimization',
        'Industry Applications'
      ],
      requirements: [
        'Basic circuit theory knowledge',
        'LTspice or similar software',
        'Engineering calculator'
      ],
      features: [
        'Live circuit simulation',
        'Real-time troubleshooting',
        'Software demonstration',
        'Q&A session',
        'Recording available'
      ],
      videoUrl: 'https://meet.google.com/abc-circuit-live',
      recordingUrl: null,
      roomPassword: 'CIRCUIT2024',
      scheduleLink: 'https://calendar.google.com/event?action=TEMPLATE&tmeid=def',
      materials: [
        'Circuit Simulation Guide.pdf',
        'SPICE Models Library.zip',
        'Reference Circuits.fzz'
      ],
      instructorImage: null,
      rating: 4.9,
      totalRatings: 56,
      tags: ['Advanced', 'Simulation', 'Masterclass', 'Live Demo'],
      isEnrolled: false,
      progress: 0,
    },
    {
      id: 'LVC5',
      title: 'Machine Learning Applications - Recorded Session',
      subtitle: 'AI in engineering industries',
      instructor: 'Dr. Alex Rodriguez',
      instructorTitle: 'AI Research Scientist',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      time: '8:00 PM - 10:00 PM',
      duration: '2 hours',
      category: 'Computer Engineering',
      level: 'Advanced',
      price: 59,
      isFree: false,
      enrolled: 189,
      maxParticipants: 200,
      status: 'past',
      color: '#FF4081',
      icon: 'robot',
      description: 'Recorded live session showing practical ML applications in various engineering fields. Includes Q&A session from the live event.',
      topics: [
        'Neural Networks for Engineering',
        'Predictive Maintenance Systems',
        'Optimization Algorithms',
        'Real-world Case Studies',
        'Q&A Session'
      ],
      requirements: [
        'Basic ML knowledge',
        'Python programming skills',
        'Interest in AI applications'
      ],
      features: [
        'Full session recording',
        'Downloadable materials',
        'Q&A from live session',
        'Certificate available',
        'Lifetime access'
      ],
      videoUrl: null,
      recordingUrl: 'https://storage.courses/ml-engineering-live-2024',
      roomPassword: null,
      scheduleLink: null,
      materials: [
        'Session Recording.mp4',
        'Presentation Slides.pdf',
        'Code Repository.zip',
        'Additional Resources.pdf'
      ],
      instructorImage: null,
      rating: 4.8,
      totalRatings: 167,
      tags: ['Recorded', 'AI', 'Advanced', 'Case Studies'],
      isEnrolled: true,
      progress: 100,
    },
    {
      id: 'LVC6',
      title: 'Sustainable Engineering Panel Discussion',
      subtitle: 'Experts discuss green technologies',
      instructor: 'Dr. Emily Green',
      instructorTitle: 'Environmental Engineering Lead',
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      time: '5:00 PM - 7:00 PM',
      duration: '2 hours',
      category: 'Environmental Engineering',
      level: 'All Levels',
      price: 0,
      isFree: true,
      enrolled: 345,
      maxParticipants: 500,
      status: 'past',
      color: '#4CAF50',
      icon: 'leaf',
      description: 'Recorded panel discussion with industry experts on sustainable engineering practices and future technologies.',
      topics: [
        'Renewable Energy Technologies',
        'Green Building Design',
        'Waste Management Solutions',
        'Sustainable Materials',
        'Industry Expert Q&A'
      ],
      requirements: [
        'Interest in sustainability',
        'Basic engineering knowledge'
      ],
      features: [
        'Expert panel discussion',
        'Interactive Q&A session',
        'Downloadable resources',
        'Free certificate',
        'Community access'
      ],
      videoUrl: null,
      recordingUrl: 'https://storage.courses/sustainable-panel-2024',
      roomPassword: null,
      scheduleLink: null,
      materials: [
        'Panel Discussion Recording.mp4',
        'Expert Presentations.pdf',
        'Additional Readings.zip',
        'Certificate of Attendance.pdf'
      ],
      instructorImage: null,
      rating: 4.7,
      totalRatings: 289,
      tags: ['Free', 'Panel', 'Recorded', 'Experts'],
      isEnrolled: true,
      progress: 100,
    },
  ];

  // Live courses come from the backend (live_courses table) with the curated
  // seed above as a fallback so the screen is never blank.
  const [liveCoursesData, setLiveCoursesData] = useState(SEED_LIVE_COURSES);

  const categories = [
    { id: 'upcoming', name: '🕒 Upcoming', count: liveCoursesData.filter(c => c.status === 'upcoming').length },
    { id: 'live', name: '🔴 Live Now', count: liveCoursesData.filter(c => c.status === 'live').length },
    { id: 'past', name: '📼 Recorded', count: liveCoursesData.filter(c => c.status === 'past').length },
    { id: 'free', name: '🎓 Free', count: liveCoursesData.filter(c => c.isFree).length },
    { id: 'enrolled', name: '📚 My Enrolled', count: userEnrolled.length },
  ];

  useEffect(() => {
    loadCourses();
    loadUserEnrollments();
    requestNotificationPermission();
  }, []);

  // Re-categorize whenever the course list changes (after backend load).
  useEffect(() => {
    categorizeSessions();
  }, [liveCoursesData]);

  // Fetch live courses from the backend; fall back to the curated seed.
  const loadCourses = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/data/live_courses?limit=100`, {
        headers: { 'x-api-key': API_KEY },
      }).then(r => r.json()).catch(() => null);
      if (res && res.success && Array.isArray(res.data) && res.data.length) {
        setLiveCoursesData(res.data.map(jvMapCourse));
      }
      // else keep the seed already in state
    } catch (e) {
      // keep seed
    } finally {
      setRefreshing(false);
    }
  };

  const onRefresh = () => { setRefreshing(true); loadCourses(); loadUserEnrollments(); };

  const requestNotificationPermission = async () => {
    const { status } = await Notifications.requestPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission required', 'Please enable notifications for session reminders');
    }
  };

  const loadUserEnrollments = async () => {
    try {
      const enrolled = await AsyncStorage.getItem('userEnrolledLiveCourses');
      if (enrolled) {
        setUserEnrolled(JSON.parse(enrolled));
      }
    } catch (error) {
      console.error('Error loading enrollments:', error);
    }
  };

  const categorizeSessions = () => {
    const now = new Date();
    
    const upcoming = liveCoursesData.filter(course => 
      course.status === 'upcoming' && course.date > now
    );
    
    const live = liveCoursesData.filter(course => 
      course.status === 'live'
    );
    
    const past = liveCoursesData.filter(course => 
      course.status === 'past'
    );

    setUpcomingSessions(upcoming);
    setLiveSessions(live);
    setPastSessions(past);
  };

  const getFilteredCourses = () => {
    switch (activeTab) {
      case 'upcoming':
        return upcomingSessions;
      case 'live':
        return liveSessions;
      case 'past':
        return pastSessions;
      case 'free':
        return liveCoursesData.filter(course => course.isFree);
      case 'enrolled':
        return liveCoursesData.filter(course => userEnrolled.includes(course.id));
      default:
        return liveCoursesData;
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setLoading(true);
      // Simulate search
      setTimeout(() => {
        setLoading(false);
        Alert.alert('Search Results', `Found sessions for "${searchQuery}"`);
      }, 1000);
    }
  };

  const handleEnroll = (course) => {
    if (course.isFree) {
      doEnroll(course);
      return;
    }
    const priceNum = Number(course.price);
    if (!priceNum || priceNum <= 0) {
      Alert.alert(
        'Price unavailable',
        'We could not determine the price of this course. Please go back and try again.'
      );
      return;
    }
    const scenario = {
      id:            `live_${course.id}`,
      type:          'live_course',
      category:      'learning',
      name:          course.title,
      description:   `Enrollment in live course "${course.title}"`,
      amountInPence: Math.round(priceNum * 100),
      currency:      'GBP',
      metadata: {
        course_id:    course.id,
        course_title: course.title,
        instructor:   course.instructor,
      },
      successRoute:   'LiveVideoCoursesScreen',
      successParams:  { enrollLiveCourseId: course.id },
      successMessage: `You're enrolled in "${course.title}".`,
    };
    navigation.navigate('PaymentScreen', { scenario });
  };

  // Persist a (free) enrollment immediately.
  const doEnroll = async (course) => {
    try {
      setLoading(true);
      const updatedEnrolled = [...userEnrolled, course.id];
      await AsyncStorage.setItem('userEnrolledLiveCourses', JSON.stringify(updatedEnrolled));
      setUserEnrolled(updatedEnrolled);

      Alert.alert(
        'Enrollment Successful!',
        `You've enrolled in "${course.title}"`,
        [
          { text: 'OK', onPress: () => { scheduleNotification(course); } },
          { text: 'Add to Calendar', onPress: () => addToCalendar(course) }
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to enroll. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // After a successful payment, PaymentScreen routes back here with the enrolled
  // course id. Persist it (reading storage fresh to avoid clobbering other
  // enrollments) and schedule the session reminder.
  useEffect(() => {
    const id = route?.params?.enrollLiveCourseId;
    if (id == null) return;
    (async () => {
      try {
        const raw = await AsyncStorage.getItem('userEnrolledLiveCourses');
        const list = raw ? JSON.parse(raw) : [];
        if (!list.includes(id)) {
          const updated = [...list, id];
          await AsyncStorage.setItem('userEnrolledLiveCourses', JSON.stringify(updated));
          setUserEnrolled(updated);
        }
        const course = liveCoursesData.find(c => c.id === id);
        if (course) scheduleNotification(course);
      } catch (e) {
        console.error('Post-payment enrollment persist failed:', e);
      } finally {
        navigation.setParams({ enrollLiveCourseId: undefined });
      }
    })();
  }, [route?.params?.enrollLiveCourseId]);

  const scheduleNotification = async (course) => {
    try {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: `⏰ Reminder: ${course.title}`,
          body: `Starts in 15 minutes! Join: ${course.videoUrl}`,
          data: { courseId: course.id, url: course.videoUrl },
        },
        trigger: {
          seconds: Math.max(0, (course.date.getTime() - Date.now()) / 1000 - 15 * 60),
        },
      });
    } catch (error) {
      console.error('Error scheduling notification:', error);
    }
  };

  const addToCalendar = async (course) => {
    try {
      const { status } = await Calendar.requestCalendarPermissionsAsync();
      if (status === 'granted') {
        const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
        const defaultCalendar = calendars.find(c => c.allowsModifications) || calendars[0];
        
        if (defaultCalendar) {
          const eventId = await Calendar.createEventAsync(defaultCalendar.id, {
            title: course.title,
            startDate: course.date,
            endDate: new Date(course.date.getTime() + 2 * 60 * 60 * 1000), // 2 hours
            location: course.videoUrl,
            notes: `Live Course: ${course.description}\nPassword: ${course.roomPassword}\nMaterials: ${course.materials.join(', ')}`,
            alarms: [{ relativeOffset: -15 }], // 15 minutes before
          });
          
          Alert.alert('Added to Calendar', 'Session added to your calendar with 15-minute reminder');
        }
      }
    } catch (error) {
      Alert.alert('Calendar Error', 'Could not add to calendar');
    }
  };

  const handleJoinSession = (course) => {
    if (course.status === 'past') {
      navigation.navigate('RecordedSession', { course });
    } else if (course.status === 'live') {
      setSelectedCourse(course);
      setShowJoinModal(true);
    } else if (course.isEnrolled) {
      Alert.alert(
        'Session Details',
        `${course.title}\n\nDate: ${course.date.toLocaleDateString()}\nTime: ${course.time}\n\nWould you like to:`,
        [
          { text: 'Set Reminder', onPress: () => scheduleNotification(course) },
          { text: 'Add to Calendar', onPress: () => addToCalendar(course) },
          { text: 'View Materials', onPress: () => navigation.navigate('CourseMaterials', { course }) },
          { text: 'Cancel', style: 'cancel' }
        ]
      );
    } else {
      Alert.alert(
        'Enroll Required',
        `You need to enroll in "${course.title}" first`,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Enroll Now', onPress: () => handleEnroll(course) }
        ]
      );
    }
  };

  const handleQuickJoin = () => {
    if (selectedCourse) {
      navigation.navigate('LiveVideoSession', { course: selectedCourse });
      setShowJoinModal(false);
    }
  };

  const renderLiveCourseCard = ({ item }) => {
    const isUpcoming = item.status === 'upcoming';
    const isLive = item.status === 'live';
    const isPast = item.status === 'past';
    const enrollmentPercentage = (item.enrolled / item.maxParticipants) * 100;

    return (
      <TouchableOpacity
        style={styles.courseCard}
        onPress={() => navigation.navigate('LiveCourseDetails', { course: item })}
      >
        <LinearGradient
          colors={[item.color, `${item.color}DD`]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.courseHeader}
        >
          <View style={styles.statusBadge}>
            <View style={[
              styles.statusDot,
              isLive && styles.liveDot,
              isUpcoming && styles.upcomingDot,
              isPast && styles.pastDot
            ]}>
              <Text style={styles.statusText}>
                {isLive ? '🔴 LIVE' : isUpcoming ? '⏰ UPCOMING' : '📼 RECORDED'}
              </Text>
            </View>
          </View>
          
          <View style={styles.headerContent}>
            <View style={styles.courseIcon}>
              <MaterialCommunityIcons name={item.icon} size={36} color="#ffffff" />
            </View>
            <View style={styles.titleContainer}>
              <Text style={styles.courseTitle} numberOfLines={2}>{item.title}</Text>
              <Text style={styles.courseSubtitle} numberOfLines={1}>{item.subtitle}</Text>
            </View>
          </View>

          {isLive && (
            <View style={styles.liveIndicator}>
              <View style={styles.pulsingDot} />
              <Text style={styles.liveNowText}>LIVE NOW</Text>
            </View>
          )}
        </LinearGradient>

        <View style={styles.courseBody}>
          <View style={styles.instructorRow}>
            <View style={styles.instructorAvatar}>
              <MaterialCommunityIcons name="account" size={20} color="#0e4e96" />
            </View>
            <View style={styles.instructorInfo}>
              <Text style={styles.instructorName}>{item.instructor}</Text>
              <Text style={styles.instructorTitle}>{item.instructorTitle}</Text>
            </View>
          </View>

          <View style={styles.detailsGrid}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="calendar" size={16} color="#64748B" />
              <Text style={styles.detailText}>
                {isPast ? 'Recorded' : item.date.toLocaleDateString()}
              </Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
              <Text style={styles.detailText}>{item.time}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="timer" size={16} color="#64748B" />
              <Text style={styles.detailText}>{item.duration}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="chart-bar" size={16} color="#64748B" />
              <Text style={styles.detailText}>{item.level}</Text>
            </View>
          </View>

          <View style={styles.enrollmentSection}>
            <View style={styles.enrollmentHeader}>
              <MaterialCommunityIcons name="account-group" size={16} color="#0e4e96" />
              <Text style={styles.enrollmentText}>
                {item.enrolled}/{item.maxParticipants} enrolled
              </Text>
              <Text style={styles.enrollmentPercentage}>{Math.round(enrollmentPercentage)}% Full</Text>
            </View>
            <View style={styles.enrollmentBar}>
              <View 
                style={[
                  styles.enrollmentFill, 
                  { width: `${enrollmentPercentage}%` },
                  enrollmentPercentage > 90 && styles.fullEnrollment
                ]} 
              />
            </View>
          </View>

          <View style={styles.tagsContainer}>
            {item.tags.slice(0, 3).map((tag, index) => (
              <View key={index} style={styles.tag}>
                <Text style={styles.tagText}>{tag}</Text>
              </View>
            ))}
          </View>

          <View style={styles.actionRow}>
            <View style={styles.priceContainer}>
              <Text style={[
                styles.priceText,
                item.isFree && styles.freePrice
              ]}>
                {item.isFree ? 'FREE' : `£${item.price}`}
              </Text>
              <View style={styles.ratingContainer}>
                <FontAwesome5 name="star" size={12} color="#fdb913" />
                <Text style={styles.ratingText}>{item.rating} ({item.totalRatings})</Text>
              </View>
            </View>
            
            <TouchableOpacity
              style={[
                styles.actionButton,
                isLive && styles.joinButton,
                isPast && styles.watchButton,
                !item.isEnrolled && styles.enrollButton
              ]}
              onPress={() => handleJoinSession(item)}
            >
              <Text style={styles.actionButtonText}>
                {isLive ? 'JOIN NOW' : 
                 isPast ? 'WATCH RECORDING' :
                 item.isEnrolled ? 'VIEW DETAILS' : 'ENROLL NOW'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderCategoryItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.categoryItem,
        activeTab === item.id && styles.activeCategoryItem,
      ]}
      onPress={() => setActiveTab(item.id)}
    >
      <Text style={[
        styles.categoryText,
        activeTab === item.id && styles.activeCategoryText,
      ]}>
        {item.name}
      </Text>
      <View style={styles.categoryCount}>
        <Text style={styles.categoryCountText}>{item.count}</Text>
      </View>
    </TouchableOpacity>
  );

  const renderJoinModal = () => (
    <Modal
      visible={showJoinModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowJoinModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <LinearGradient
            colors={selectedCourse?.color ? [selectedCourse.color, `${selectedCourse.color}CC`] : ['#0e4e96', '#1a6ec3']}
            style={styles.modalHeader}
          >
            <TouchableOpacity 
              style={styles.modalCloseButton}
              onPress={() => setShowJoinModal(false)}
            >
              <Ionicons name="close" size={24} color="#ffffff" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Join Live Session</Text>
          </LinearGradient>

          <ScrollView style={styles.modalBody}>
            <View style={styles.modalCourseInfo}>
              <Text style={styles.modalCourseTitle}>{selectedCourse?.title}</Text>
              <Text style={styles.modalCourseSubtitle}>{selectedCourse?.subtitle}</Text>
              
              <View style={styles.modalDetails}>
                <View style={styles.modalDetailItem}>
                  <MaterialCommunityIcons name="account" size={20} color="#0e4e96" />
                  <Text style={styles.modalDetailText}>{selectedCourse?.instructor}</Text>
                </View>
                <View style={styles.modalDetailItem}>
                  <MaterialCommunityIcons name="clock-outline" size={20} color="#0e4e96" />
                  <Text style={styles.modalDetailText}>{selectedCourse?.duration}</Text>
                </View>
                <View style={styles.modalDetailItem}>
                  <MaterialCommunityIcons name="account-group" size={20} color="#0e4e96" />
                  <Text style={styles.modalDetailText}>{selectedCourse?.enrolled} participants</Text>
                </View>
              </View>

              <View style={styles.roomInfo}>
                <Text style={styles.roomInfoTitle}>Meeting Room Details</Text>
                <View style={styles.roomDetail}>
                  <MaterialCommunityIcons name="link" size={16} color="#64748B" />
                  <Text style={styles.roomDetailText} selectable>
                    {selectedCourse?.videoUrl}
                  </Text>
                </View>
                {selectedCourse?.roomPassword && (
                  <View style={styles.roomDetail}>
                    <MaterialCommunityIcons name="lock" size={16} color="#64748B" />
                    <Text style={styles.roomDetailText}>Password: {selectedCourse.roomPassword}</Text>
                  </View>
                )}
              </View>

              <View style={styles.featuresList}>
                <Text style={styles.featuresTitle}>Session Features:</Text>
                {selectedCourse?.features?.slice(0, 4).map((feature, index) => (
                  <View key={index} style={styles.featureItem}>
                    <MaterialCommunityIcons name="check-circle" size={16} color="#10b981" />
                    <Text style={styles.featureText}>{feature}</Text>
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>

          <View style={styles.modalFooter}>
            <TouchableOpacity 
              style={styles.cancelButton}
              onPress={() => setShowJoinModal(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.joinSessionButton}
              onPress={handleQuickJoin}
            >
              <MaterialCommunityIcons name="video" size={20} color="#ffffff" />
              <Text style={styles.joinSessionText}>Join Session</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderHeader = () => (
    <View>
      <View style={styles.headerHero}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          style={styles.heroGradient}
        >
          <Text style={styles.heroTitle}>🎥 Live Interactive Courses</Text>
          <Text style={styles.heroSubtitle}>
            Join real-time engineering sessions with expert instructors
          </Text>
        </LinearGradient>
      </View>

      {/* Quick Stats */}
      <View style={styles.statsContainer}>
        <LinearGradient
          colors={['#4facfe', '#00f2fe']}
          style={styles.statCard}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          <MaterialCommunityIcons name="video" size={28} color="#ffffff" />
          <Text style={styles.statNumber}>{liveCoursesData.length}</Text>
          <Text style={styles.statLabel}>Live Sessions</Text>
        </LinearGradient>

        <LinearGradient
          colors={['#43e97b', '#38f9d7']}
          style={styles.statCard}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          <MaterialCommunityIcons name="account-group" size={28} color="#ffffff" />
          <Text style={styles.statNumber}>1,140</Text>
          <Text style={styles.statLabel}>Active Learners</Text>
        </LinearGradient>

        <LinearGradient
          colors={['#fa709a', '#fee140']}
          style={styles.statCard}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          <MaterialCommunityIcons name="clock" size={28} color="#ffffff" />
          <Text style={styles.statNumber}>24/7</Text>
          <Text style={styles.statLabel}>Support</Text>
        </LinearGradient>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={22} color="#64748B" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search live sessions, topics, instructors..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          onSubmitEditing={handleSearch}
          placeholderTextColor="#94a3b8"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={22} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>

      {/* Categories */}
      <View style={styles.categoriesContainer}>
        <FlatList
          data={categories}
          renderItem={renderCategoryItem}
          keyExtractor={item => item.id}
          horizontal
          showsHorizontalScrollIndicator={false}
        />
      </View>

      {/* Section Header */}
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>
          {activeTab === 'live' ? '🔴 Live Now' :
           activeTab === 'upcoming' ? '🕒 Upcoming Sessions' :
           activeTab === 'past' ? '📼 Recorded Sessions' :
           activeTab === 'free' ? '🎓 Free Sessions' : '📚 My Enrolled Sessions'}
        </Text>
        <TouchableOpacity 
          style={styles.scheduleButton}
          onPress={() => navigation.navigate('ScheduleView')}
        >
          <MaterialCommunityIcons name="calendar-clock" size={18} color="#0e4e96" />
          <Text style={styles.scheduleText}>View Schedule</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {/* Main Header */}
      <LinearGradient
        colors={['#667eea', '#764ba2']}
        style={styles.mainHeader}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Live Video Courses</Text>
        <TouchableOpacity 
          style={styles.notificationButton}
          onPress={() => navigation.navigate('Notifications')}
        >
          <Ionicons name="notifications-outline" size={24} color="#ffffff" />
          <View style={styles.notificationBadge}>
            <Text style={styles.notificationCount}>3</Text>
          </View>
        </TouchableOpacity>
      </LinearGradient>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading sessions...</Text>
        </View>
      ) : (
        <FlatList
          data={getFilteredCourses()}
          renderItem={renderLiveCourseCard}
          keyExtractor={item => item.id}
          ListHeaderComponent={renderHeader}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          ListEmptyComponent={() => (
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name="video-off" size={72} color="#cbd5e1" />
              <Text style={styles.emptyTitle}>No sessions found</Text>
              <Text style={styles.emptyText}>
                {activeTab === 'live' ? 'No live sessions at the moment. Check back soon!' :
                 activeTab === 'upcoming' ? 'No upcoming sessions scheduled' :
                 'No sessions match your criteria'}
              </Text>
              <TouchableOpacity 
                style={styles.emptyButton}
                onPress={() => setActiveTab('upcoming')}
              >
                <Text style={styles.emptyButtonText}>Browse Upcoming Sessions</Text>
              </TouchableOpacity>
            </View>
          )}
        />
      )}

      {/* Quick Actions FAB */}
      <TouchableOpacity 
        style={styles.fab}
        onPress={() => navigation.navigate('QuickSchedule')}
      >
        <MaterialCommunityIcons name="plus" size={28} color="#ffffff" />
      </TouchableOpacity>

      {renderJoinModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  mainHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
  notificationButton: {
    padding: 4,
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#ef4444',
    borderRadius: 10,
    width: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationCount: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#64748B',
  },
  listContainer: {
    paddingBottom: 100,
  },
  headerHero: {
    marginHorizontal: 20,
    marginTop: 20,
    borderRadius: 20,
    overflow: 'hidden',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
  },
  heroGradient: {
    padding: 24,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
  },
  heroSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    lineHeight: 22,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    marginTop: 20,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    marginHorizontal: 5,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 8,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    marginHorizontal: 20,
    marginTop: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 16,
    color: '#1f2937',
  },
  categoriesContainer: {
    marginTop: 20,
    paddingHorizontal: 20,
  },
  categoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e9eef4',
    borderRadius: 20,
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginRight: 10,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  activeCategoryItem: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  categoryText: {
    color: '#0e4e96',
    fontWeight: '600',
    fontSize: 14,
  },
  activeCategoryText: {
    color: '#ffffff',
  },
  categoryCount: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  categoryCountText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginTop: 24,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  scheduleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  scheduleText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 4,
    fontWeight: '500',
  },
  courseCard: {
    backgroundColor: '#ffffff',
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 20,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    overflow: 'hidden',
  },
  courseHeader: {
    padding: 20,
    position: 'relative',
  },
  statusBadge: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  statusDot: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    elevation: 2,
  },
  liveDot: {
    backgroundColor: '#ef4444',
  },
  upcomingDot: {
    backgroundColor: '#f59e0b',
  },
  pastDot: {
    backgroundColor: '#6b7280',
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  courseIcon: {
    marginRight: 16,
  },
  titleContainer: {
    flex: 1,
  },
  courseTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  courseSubtitle: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
  },
  liveIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  pulsingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#ffffff',
    marginRight: 8,
  },
  liveNowText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  courseBody: {
    padding: 20,
  },
  instructorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  instructorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e9eef4',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  instructorInfo: {
    flex: 1,
  },
  instructorName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  instructorTitle: {
    fontSize: 12,
    color: '#6b7280',
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
    gap: 12,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    flex: 1,
    minWidth: '45%',
  },
  detailText: {
    fontSize: 12,
    color: '#4b5563',
    marginLeft: 6,
  },
  enrollmentSection: {
    marginBottom: 16,
  },
  enrollmentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  enrollmentText: {
    fontSize: 14,
    color: '#4b5563',
    marginLeft: 6,
    flex: 1,
  },
  enrollmentPercentage: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
  },
  enrollmentBar: {
    height: 6,
    backgroundColor: '#e5e7eb',
    borderRadius: 3,
    overflow: 'hidden',
  },
  enrollmentFill: {
    height: '100%',
    backgroundColor: '#0e4e96',
    borderRadius: 3,
  },
  fullEnrollment: {
    backgroundColor: '#ef4444',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
    gap: 8,
  },
  tag: {
    backgroundColor: '#f0f9ff',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#dbeafe',
  },
  tagText: {
    fontSize: 11,
    color: '#0e4e96',
    fontWeight: '500',
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceContainer: {
    flex: 1,
  },
  priceText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  freePrice: {
    color: '#10b981',
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  ratingText: {
    fontSize: 12,
    color: '#6b7280',
    marginLeft: 4,
  },
  actionButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    elevation: 2,
  },
  joinButton: {
    backgroundColor: '#ef4444',
  },
  watchButton: {
    backgroundColor: '#6b7280',
  },
  enrollButton: {
    backgroundColor: '#10b981',
  },
  actionButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  emptyTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1f2937',
    marginTop: 24,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
  },
  emptyButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 8,
  },
  emptyButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 30,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    width: '90%',
    maxHeight: '80%',
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  modalCloseButton: {
    marginRight: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
  },
  modalBody: {
    maxHeight: 400,
  },
  modalCourseInfo: {
    padding: 20,
  },
  modalCourseTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 8,
  },
  modalCourseSubtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginBottom: 20,
  },
  modalDetails: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  modalDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  modalDetailText: {
    fontSize: 14,
    color: '#4b5563',
    marginLeft: 12,
    flex: 1,
  },
  roomInfo: {
    backgroundColor: '#f0f9ff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#dbeafe',
  },
  roomInfoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
    marginBottom: 12,
  },
  roomDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  roomDetailText: {
    fontSize: 14,
    color: '#4b5563',
    marginLeft: 10,
    flex: 1,
  },
  featuresList: {
    marginBottom: 20,
  },
  featuresTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 12,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: '#4b5563',
    marginLeft: 10,
    flex: 1,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    marginRight: 10,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6b7280',
  },
  joinSessionButton: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    backgroundColor: '#ef4444',
    borderRadius: 8,
    marginLeft: 10,
  },
  joinSessionText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginLeft: 8,
  },
});

export default LiveVideoCoursesScreen;